/**
 * Resume Studio — optional profile photo upload.
 *
 * Only rendered by the Fresher/Experienced flows on Pro/Elite tiers.
 * The backend silently drops the photo from ATS templates for parseability.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export default function PhotoUpload({ file, onFileChange }) {
    const fileRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    useEffect(() => {
        if (!file) {
            setPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const handlePick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!/^image\/(jpeg|png|webp|jpg)$/.test(f.type)) {
            toast.error("Please upload a JPG, PNG, or WebP image");
            return;
        }
        if (f.size > 3 * 1024 * 1024) {
            toast.error("Photo too large (max 3 MB)");
            return;
        }
        onFileChange(f);
    };

    return (
        <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-4">
            <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {previewUrl ? (
                        <img src={previewUrl} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                        <span className="text-3xl text-slate-300">👤</span>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800">
                        Profile photo <span className="text-slate-400 font-normal">— optional</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                        Shown on Pro & Elite templates only. Skipped on ATS templates to keep parsing clean. Square-cropped, ≤ 3 MB.
                    </div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        data-testid="photo-upload-input"
                        onChange={handlePick}
                    />
                    <div className="mt-2 flex items-center gap-2">
                        <button
                            onClick={() => fileRef.current?.click()}
                            data-testid="photo-upload-btn"
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-700"
                        >
                            {file ? "Change photo" : "Upload photo"}
                        </button>
                        {file && (
                            <button
                                onClick={() => onFileChange(null)}
                                data-testid="photo-remove-btn"
                                className="text-xs font-semibold text-red-600 hover:underline"
                            >
                                Remove
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
